// (c) chaehongee. Confidential and proprietary.

import { useEffect } from 'react'
import type { Dispatch } from '@reduxjs/toolkit'
import type { NodeResponse } from 'models-ts/node'
import { addItem as addCanvasItem } from '../../../../store/slices/canvasSlice'

interface UseAutoAddSelectedNodeParams {
    session: { id: string; nodes?: NodeResponse[] } | null
    selectedNodeIds: string[]
    canvasItems: Array<{ id: string; src: string; mediaType?: string }>
    dispatch: Dispatch
    isSessionLoading?: boolean
}

/**
 * Hook that automatically adds the last selected node to the canvas
 * if it's not already present. This enables seamless selection-to-canvas workflow.
 */
export function useAutoAddSelectedNode({
    session,
    selectedNodeIds,
    canvasItems,
    dispatch,
    isSessionLoading = false,
}: UseAutoAddSelectedNodeParams): void {
    useEffect(() => {
        if (!session?.id) return
        // On session entry we may auto-select a node while the session is still loading/hydrating.
        // Avoid auto-adding that single node to the canvas before the full sync runs; it can
        // cause a one-off persisted placement that leaves the last node "out of place".
        if (isSessionLoading) return
        if (selectedNodeIds.length === 0) return

        const chosenId = selectedNodeIds[selectedNodeIds.length - 1]
        const node = (session?.nodes ?? []).find((n: NodeResponse) => n?.id === chosenId)
        if (!node?.id || !node?.image_path) return

        const exists = canvasItems.some((it) => it.id === node.id)
        if (!exists) {
            dispatch(
                addCanvasItem({
                    sessionId: session.id,
                    item: { id: node.id, src: node.image_path, mediaType: node.type ?? 'image' },
                })
            )
        }
    }, [session?.id, session?.nodes, selectedNodeIds, canvasItems, dispatch, isSessionLoading])
}

